import { dispatchAction } from '@ardoq/rxbeach';
import { clearAndSetLoadedState } from 'loadedState/actions';
import { requestMainView } from 'streams/views/mainContent/requestMainView';
import {
  applyFormattingAndLabelRules,
  applyGroupingRules,
} from 'traversals/applyViewpointPerspectives';
import { applyViewpointViewSettings } from 'traversals/applyViewpointViewSettings';
import {
  APIViewpointAttributes,
  GroupingRule,
  LoadedState,
  SYMBOL_TRANSACTION_ID,
  ViewIds,
  WithTransactionId,
} from '@ardoq/api-types';
import { newHistoryEntryRequested } from 'canvas/contextToolBar/loadedStateHistory/loadedStateHistoryActions';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';
import { toRenderedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { v4 as uuid } from 'uuid';
import { ChangesToAppViewState } from './rebuildStateFromPoppedHistoryUtils';

export type SetApplicationViewStateParams = WithTransactionId & {
  conditionalFormatting: APIViewpointAttributes['conditionalFormatting'];
  groupBys: GroupingRule[];
  loadedStates: LoadedState[];
  viewNameCandidate: ViewIds;
  /**
   * Set view settings to null if you don't have valid settings at hand.
   * If null, the view settings get set correctly in the reducer.
   * All view settings are in the current history stream.
   */
  viewSettings: Partial<Record<ViewIds, Record<string, any>>> | null;
};

type GroupingFormattingAndViewSettings = Pick<
  SetApplicationViewStateParams,
  'conditionalFormatting' | 'groupBys' | 'viewSettings'
> & {
  viewName: ViewIds;
  transactionId: string;
};

export const applyGroupingFormattingAndViewSettings = (
  {
    conditionalFormatting,
    groupBys,
    viewName,
    viewSettings,
    transactionId,
  }: GroupingFormattingAndViewSettings,
  {
    hasConditionalFormattingChanged,
    hasGroupBysChanged,
    hasViewSettingsChanged,
  }: Pick<
    ChangesToAppViewState,
    | 'hasConditionalFormattingChanged'
    | 'hasGroupBysChanged'
    | 'hasViewSettingsChanged'
  >
): void => {
  if (hasViewSettingsChanged) {
    applyViewpointViewSettings({
      viewSettings: {
        [viewName]: {
          ...viewSettings?.[viewName],
          [SYMBOL_TRANSACTION_ID]: transactionId,
        },
      },
      viewName,
    });
  }

  if (hasGroupBysChanged) {
    applyGroupingRules({ groupingRules: groupBys, transactionId });
  }

  if (hasConditionalFormattingChanged) {
    applyFormattingAndLabelRules({
      rules: conditionalFormatting,
      transactionId,
    });
  }
};

export const setApplicationViewState = (
  {
    conditionalFormatting,
    groupBys,
    loadedStates,
    viewNameCandidate,
    viewSettings,
    transactionId = uuid(),
  }: SetApplicationViewStateParams,
  changes: ChangesToAppViewState | undefined = {
    hasConditionalFormattingChanged: true,
    hasGroupBysChanged: true,
    hasLoadedStateChanged: true,
    hasViewNameChanged: true,
    hasViewSettingsChanged: true,
    hasChanges: true,
  }
) => {
  const viewName = isViewpointModeView(viewNameCandidate)
    ? viewNameCandidate
    : ViewIds.BLOCKS;
  // The newHistoryEntryRequested will create a new history entry in the history
  // stream with the given transaction id and view name. All the actual state
  // updates with the same transaction id will then be subsumed in that history
  // entry, meaning they will result in a history replace instead of a history
  // push.
  dispatchAction(newHistoryEntryRequested({ viewName, transactionId }));

  if (changes.hasLoadedStateChanged) {
    dispatchAction(
      clearAndSetLoadedState({
        loadedStates,
        transactionId,
        viewName: viewIdToUserFriendlyName[viewName],
      })
    );
  }

  requestMainView({
    activeTabId: toRenderedViewpointViewId(viewName),
    isViewpointMode: true,
  });

  applyGroupingFormattingAndViewSettings(
    { conditionalFormatting, groupBys, viewName, viewSettings, transactionId },
    changes
  );
};
